import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'
import { isNil } from 'lodash'
import moment from 'moment'
import PlacesAutocomplete, { geocodeByPlaceId, geocodeByAddress, getLatLng } from 'react-places-autocomplete'
import './index.scss'
import Wind from '../../lib/api/wind'
import ThermalsApi from '../../lib/api/thermals'
import weatherCenterApi from '../../lib/api/weatherCenter'
import { compass16, locationOffset, placeLabel, speedUnit, speedValue } from '../../lib/weatherCenter'
import { frameField } from '../../lib/thermals/day'
import { userHasPro } from '../../lib/util'
import { blockFeatures, unBlockFeatures } from '../../action/features-blocked-actions'
import { activeFeatureStore } from '../terrain-analysis/active-feature-store'
import SubPopup from '../main-ui/common/hunt-wind/sub-popup'
import WindFlow from '../main-ui/common/hunt-wind/wind-flow'
import { heatTrend } from '../main-ui/common/hunt-wind/thermals/model'
import ThermalsOverlay3D from '../thermals-tool/ThermalsOverlay3D'
import MapMarks from './MapMarks'
import { checkWindStore } from './store'
import { surfaceFor } from './surface'
import {
  coordsLabel, dayTimeline, dirLong, distanceM, downwind, forecastDays, forecastLocked, hourlyTimeline, localHour, nearestFrame,
  plumeBearing, thermalsTurning, ticks, whenLabel, withObservation
} from './model'

// HuntWind's Check Wind on the map: drop a wind cone, read the hour's wind and where scent drifts, pick a day
// and scrub its hours, and turn on Thermals to see slopes bend the scent. One component for both maps.
// The day picker replaces the design's Hourly | 7-Day switch (Matt, 3 Nov); product may send it back to the
// design, so it is one commit to revert.

const PLACE_ZOOM = 16
// The cone counts as at the user's location within this many metres of it.
const AT_GPS_M = 30
// Observed hours on the hourly slider before Now.
const PAST_HOURS = 2
// Today and the next seven days, from Wind/GetHourly's 15-day product. A server still on the 2-day product
// refuses it, and the 24 hours it always served are fetched instead.
const FORECAST_HOURS = 192
const PICKER_DAYS = 8
// Thermals past today + 2 are the terrain's expected pattern: cloud cover, which drives them, is weak that far out.
const PRECISE_DAYS = 2
// RE-CENTER shows once the cone is this far off the map's centre.
const RECENTER_PX = 6
// Thermals over a mile round the cone.
const THERMALS_RADIUS_KM = 1.6
const THERMALS_CELL_M = 60
// As the design: the plume is the cone's tiers bent along the API's path, over the moving wind; no slope
// arrows and no plume of the overlay's own.
const THERMALS_SHOW = { arrows: false, flow: true, plume: false }
const HEAT_WORDS = { warming: 'Thermals rising', cooling: 'Thermals sinking', turning: 'Thermals turning' }
// Percent: the moving wind shows the map through it.
const THERMALS_OPACITY = 85

const MoveIcon = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
    <path d='M12 2 8.5 5.5h2.5V11H5.5V8.5L2 12l3.5 3.5V13H11v5.5H8.5L12 22l3.5-3.5H13V13h5.5v2.5L22 12l-3.5-3.5V11H13V5.5h2.5Z' />
  </svg>
)

const HeatIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' aria-hidden='true'>
    <path d='M7 20c-2-2.5 2-5 0-8s2-5.5 0-8M12 20c-2-2.5 2-5 0-8s2-5.5 0-8M17 20c-2-2.5 2-5 0-8s2-5.5 0-8' />
  </svg>
)

const GpsIcon = ({ color }) => (
  <svg width='18' height='18' viewBox='0 0 24 24' aria-hidden='true'>
    <circle cx='12' cy='12' r='9' fill='none' stroke={color} strokeWidth='2.2' />
    <circle cx='12' cy='12' r='5' fill={color} />
  </svg>
)

function CheckWind({ kind, map, mapReference, is3d, token, user, units, blockFeatures, unBlockFeatures }) {
  const theMap = kind === '3d' ? map : mapReference
  const [open, setOpen] = useState(checkWindStore.open)
  const active = open && !!theMap && (kind === '3d') === !!is3d

  const [surface, setSurface] = useState(null)
  const [, setTick] = useState(0)
  const [origin, setOrigin] = useState(null)
  const [placing, setPlacing] = useState(true)
  const [hourly, setHourly] = useState(null)
  const [obs, setObs] = useState(null)
  const [gps, setGps] = useState(null)
  const [query, setQuery] = useState('')
  const [failed, setFailed] = useState(false)
  const [place, setPlace] = useState(null)
  const [day, setDay] = useState(0)
  const [k, setK] = useState(0)
  const [thermOn, setThermOn] = useState(false)
  const [thermDays, setThermDays] = useState({})
  const [thermFailed, setThermFailed] = useState(false)
  const [proOpen, setProOpen] = useState(false)
  const [toast, setToast] = useState(null)
  const pro = userHasPro(user)

  useEffect(() => checkWindStore.onChange(() => setOpen(checkWindStore.open)), [])

  // Mount on the map while open; start every visit choosing a spot.
  useEffect(() => {
    if (!active) return undefined
    const s = surfaceFor(kind, theMap)
    setSurface(s)
    document.body.classList.add('check-wind-open')
    if (kind === '3d') activeFeatureStore.set('checkWind')
    return () => {
      s.setCursor(null)
      s.destroy()
      setSurface(null)
      document.body.classList.remove('check-wind-open')
      if (activeFeatureStore.active === 'checkWind') activeFeatureStore.set(null)
      setOrigin(null); setPlacing(true); setHourly(null); setObs(null); setGps(null); setQuery('')
      setFailed(false); setPlace(null)
      setDay(0); setK(0); setThermOn(false); setThermDays({}); setThermFailed(false); setProOpen(false)
    }
  }, [active, kind, theMap])

  // Redraw the marks as the map moves, once a frame.
  useEffect(() => {
    if (!surface) return undefined
    let frame = 0
    const off = surface.onMove(() => {
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; setTick(t => t + 1) })
    })
    return () => { off(); cancelAnimationFrame(frame) }
  }, [surface])

  const drop = (ll, fromGps) => {
    setGps(fromGps ? ll : gps)
    setOrigin(ll)
    setPlacing(false)
    setDay(0)
    setK(0)
    surface.centreOn(ll, PLACE_ZOOM)
  }

  // Choosing a spot: the crosshair marks the map's centre; a click drops the cone where it lands.
  useEffect(() => {
    if (!surface || !placing) return undefined
    surface.setCursor('crosshair')
    if (kind !== '3d') blockFeatures()
    const off = surface.onClick(ll => drop(ll))
    return () => {
      off()
      surface.setCursor(null)
      if (kind !== '3d') unBlockFeatures()
    }
  }, [surface, placing])

  useEffect(() => {
    if (!origin) return undefined
    let live = true
    setHourly(null); setObs(null); setFailed(false); setPlace(null); setThermDays({}); setThermFailed(false)
    const req = { lat: origin.lat, lng: origin.lng, token }
    Wind.fetchHourly({ ...req, pastHours: PAST_HOURS, hours: FORECAST_HOURS })
      .catch(() => Wind.fetchHourly({ ...req, pastHours: PAST_HOURS }))
      .then(h => { if (live) setHourly(h || []) })
      .catch(err => {
        console.error('Check Wind could not fetch wind: ', err)
        if (live) setFailed(true)
      })
    // The current observation: Now's wind when it is fresh, and the cone's time zone. Optional.
    weatherCenterApi.observation({ lat: origin.lat, lng: origin.lng, token })
      .then(o => { if (live) setObs(o || null) })
      .catch(() => {})
    weatherCenterApi.reverseGeocode(origin)
      .then(results => { if (live) setPlace(placeLabel(results)) })
      .catch(() => {})
    return () => { live = false }
  }, [origin, token])

  const tz = useMemo(() => locationOffset(obs && obs.timeStamp, obs && obs.sunrise), [obs])
  // `k` counts from Now: negative on the observed hours before it.
  const timeline = useMemo(() => {
    if (!hourly) return null
    const { stops, nowIndex } = hourlyTimeline(hourly, PAST_HOURS)
    if (stops.length) stops[nowIndex] = withObservation(stops[nowIndex], obs)
    return { stops, nowIndex }
  }, [hourly, obs])
  const hourlyStops = timeline && timeline.stops
  const dayList = useMemo(() => forecastDays(timeline, tz, PICKER_DAYS), [timeline, tz])
  const picked = dayList[Math.min(day, Math.max(0, dayList.length - 1))]
  const dayStops = useMemo(() => (picked ? dayTimeline(timeline, tz, picked.date, day === 0) : null), [timeline, tz, picked, day])
  // 'hourly' is today, with Now; 'day' a later day.
  const mode = day === 0 ? 'hourly' : 'day'
  const entries = dayStops ? dayStops.stops : hourlyStops
  const nowIndex = dayStops ? dayStops.nowIndex : timeline ? timeline.nowIndex : 0
  const base = Math.max(0, nowIndex)
  const entry = entries && entries[Math.max(0, Math.min(k + base, entries.length - 1))]
  const time = entry && entry.timeStamp ? moment.utc(entry.timeStamp).valueOf() : null
  const thermalsLive = thermOn && pro && !!origin && !placing

  // Thermals for the hour: the picked day's Thermals/Day (today's as the service reckons it), the frame nearest
  // the slider's hour.
  const thermDate = picked ? picked.date : null
  const thermDay = thermDate ? thermDays[thermDate] : null
  useEffect(() => {
    if (!thermalsLive || !thermDate || thermDay) return undefined
    let live = true
    setThermFailed(false)
    ThermalsApi.fetchDay({
      lat: origin.lat, lng: origin.lng, date: day === 0 ? undefined : thermDate,
      radiusKm: THERMALS_RADIUS_KM, cellM: THERMALS_CELL_M, token,
      // Check Wind draws only the combined wind and the plume, so not the slope-wind grids.
      slopeWind: false, patient: true
    })
      .then(d => { if (live) setThermDays(all => ({ ...all, [thermDate]: d })) })
      .catch(err => {
        console.error('Check Wind could not fetch thermals: ', err)
        if (live) setThermFailed(true)
      })
    return () => { live = false }
  }, [thermalsLive, thermDate, !!thermDay, origin, token])

  const field = useMemo(() => {
    if (!thermalsLive || time == null || !thermDay) return null
    const i = nearestFrame(thermDay, time)
    return i != null ? frameField(thermDay, i) : null
  }, [thermalsLive, time, thermDay])
  const thermLoading = thermalsLive && !thermDay && !thermFailed

  useEffect(() => {
    if (!toast) return undefined
    const t = setTimeout(() => setToast(null), 2600)
    return () => clearTimeout(t)
  }, [toast])

  if (!active || !surface) return null

  const close = () => checkWindStore.set(false)
  const setSlider = v => {
    if (forecastLocked(v, pro)) { setProOpen(true); return }
    setK(v)
  }
  // A later day is forecast, so Pro only. It opens on the hour of day the slider was on, else its first hour.
  const pickDay = i => {
    if (i === day) return
    if (i > 0 && !pro) { setProOpen(true); return }
    const next = dayTimeline(timeline, tz, dayList[i].date, i === 0)
    const hour = entry ? localHour(entry, tz, mode) : null
    const at = hour == null ? -1 : next.stops.findIndex(e => Math.floor(localHour(e, tz, 'hourly')) === Math.floor(hour))
    setDay(i)
    setK(i === 0 ? 0 : Math.max(0, at))
  }
  const tapThermals = () => {
    if (!pro) { setProOpen(true); return }
    setThermOn(!thermOn)
  }
  const placeHere = () => {
    const { width, height } = surface.size()
    const ll = surface.unproject({ x: width / 2, y: height / 2 })
    if (ll) drop(ll)
  }
  const myLocation = () => {
    if (!navigator.geolocation) { setToast('Location is not available'); return }
    navigator.geolocation.getCurrentPosition(
      pos => drop({ lat: pos.coords.latitude, lng: pos.coords.longitude }, true),
      () => setToast('Could not find your location'),
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  const loading = !placing && !hourly && !failed
  const noData = failed || (!placing && !!hourlyStops && hourlyStops.length === 0)
  const metric = units === 'metric'
  const sUnit = speedUnit(units)
  const atGps = !!gps && !!origin && distanceM(gps, origin) <= AT_GPS_M
  const wind = entry && !isNil(entry.direction) ? entry : null
  const plume = field && field.plume && field.plume.points && field.plume.points.length > 1 ? field.plume : null
  const driftBearing = plume ? plumeBearing(plume) : wind ? downwind(wind.direction) : null
  // What the slopes are doing from the ground's heat flux; before the service sent it, whether they pull
  // the scent well off the wind's line.
  const heat = field ? heatTrend(field.summary) : null
  const heatWord = heat ? HEAT_WORDS[heat]
    : plume && wind && thermalsTurning(wind.direction, plume) ? HEAT_WORDS.turning : null
  const thermalsWord = heatWord && day > PRECISE_DAYS ? `Expected: ${heatWord.toLowerCase()}` : heatWord
  const tickList = ticks(mode, entries, tz, nowIndex)
  const last = Math.max(0, (entries || []).length - 1)
  const min = -base, max = last - base
  const centre = { x: surface.size().width / 2, y: surface.size().height / 2 }
  const centreLL = placing ? surface.unproject(centre) : null
  const originPx = !placing && origin ? surface.project(origin) : null
  const offCentre = !!originPx && Math.hypot(originPx.x - centre.x, originPx.y - centre.y) > RECENTER_PX
  // A searched place moves the map under the crosshair; the user still places the cone.
  const searchPick = (address, placeId) => {
    const lookup = placeId ? geocodeByPlaceId(placeId) : geocodeByAddress(address)
    lookup.then(results => getLatLng(results[0]))
      .then(ll => { setQuery(''); surface.centreOn(ll, PLACE_ZOOM) })
      .catch(() => setToast('No matches. Try a town or ZIP.'))
  }

  return ReactDOM.createPortal(
    <>
      {!placing && origin ? (
        <MapMarks surface={surface} origin={origin} wind={wind} hour={localHour(entry, tz, mode)} path={plume} metric={metric} />
      ) : null}
      {placing ? (
        <svg className='check-wind-marks' width={centre.x * 2} height={centre.y * 2}>
          <path d={`M${centre.x},${centre.y - 18}V${centre.y + 18}M${centre.x - 18},${centre.y}H${centre.x + 18}`}
            stroke='#000' strokeWidth='4.5' strokeLinecap='round' opacity='.35' />
          <path d={`M${centre.x},${centre.y - 18}V${centre.y + 18}M${centre.x - 18},${centre.y}H${centre.x + 18}`}
            stroke='#589500' strokeWidth='2.5' strokeLinecap='round' />
        </svg>
      ) : null}
      <div className='check-wind-fade-top' />
      <div className='check-wind-fade-bottom' />

      <div className='check-wind-top'>
        <div className='check-wind-header'>
          <button className='check-wind-back' onClick={close} aria-label='Close HuntWind'>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round'><path d='M15 5l-7 7 7 7' /></svg>
          </button>
          <span className='check-wind-brand'>HuntWind<sup>™</sup></span>
          <span className='check-wind-where'>
            {placing ? 'Choose a spot on the map' : (
              <>
                {atGps ? <svg width='14' height='14' viewBox='0 0 24 24' fill='#589500' aria-hidden='true'><path d='M21 3 3 10.5l7.5 2.9L13.5 21Z' /></svg> : null}
                {place || (origin ? coordsLabel(origin) : '')}
              </>
            )}
          </span>
        </div>

        {placing ? (
          <PlacesAutocomplete value={query} onChange={setQuery} onSelect={searchPick} debounce={300} highlightFirstSuggestion>
            {({ getInputProps, suggestions, getSuggestionItemProps, loading: searching }) => (
              <div className='check-wind-search'>
                <input {...getInputProps({ placeholder: 'Search places', autoComplete: 'off', 'aria-label': 'Search places' })} />
                {suggestions.length ? (
                  <div className='check-wind-suggestions'>
                    {suggestions.map(s => (
                      <div {...getSuggestionItemProps(s, { className: `check-wind-suggestion${s.active ? ' on' : ''}` })} key={s.placeId}>
                        {s.formattedSuggestion.mainText}<span>{s.formattedSuggestion.secondaryText}</span>
                      </div>
                    ))}
                  </div>
                ) : query.trim().length > 1 && !searching ? (
                  <div className='check-wind-suggestions'><div className='check-wind-suggestion empty'>No matches. Try a town or ZIP.</div></div>
                ) : null}
              </div>
            )}
          </PlacesAutocomplete>
        ) : null}

        {!placing && wind ? (
          <div className='check-wind-readout'>
            <div>
              <div className='check-wind-big'>
                <span>{compass16(wind.direction)}</span>
                <span>{speedValue(wind.speed || 0, units)}</span>
                <span className='check-wind-unit'>{sUnit}</span>
              </div>
              <div className='check-wind-sub'>{dirLong(wind.direction)}</div>
              {!isNil(wind.gust) ? <div className='check-wind-sub strong'>Gusts {speedValue(wind.gust, units)} {sUnit}</div> : null}
            </div>
            <div className='check-wind-drift'>
              {driftBearing != null ? <span>Drifting {compass16(driftBearing)}</span> : null}
              {thermalsWord ? <span>{thermalsWord}</span> : null}
              {thermLoading ? <span className='check-wind-pending'><i className='check-wind-spinner' />Modeling thermals…</span> : null}
              {thermalsLive && thermFailed ? <span className='check-wind-muted'>Thermals unavailable</span> : null}
              {thermalsLive && !thermLoading && !thermFailed && !field ? <span className='check-wind-muted'>No thermals for this hour</span> : null}
            </div>
          </div>
        ) : null}
        {noData ? <div className='check-wind-big-msg'>No wind data<span>Could not load the forecast for this spot</span></div> : null}
      </div>

      {toast ? <div className='check-wind-toast'>{toast}</div> : null}

      {loading ? (
        <div className='check-wind-loading'><i className='check-wind-spinner' />Wind Data Loading</div>
      ) : null}

      <div className={`check-wind-dock${kind === '3d' ? ' beside-controls' : ''}`}>
        {placing ? (
          <>
            {centreLL ? <div className='check-wind-coords'>{coordsLabel(centreLL)}</div> : null}
            <div className='check-wind-place-row'>
              <button className='check-wind-btn secondary' onClick={myLocation}>
                <GpsIcon color='#E4E6E1' />My location
              </button>
              <button className='check-wind-btn primary' onClick={placeHere}>Place wind cone</button>
            </div>
            {origin ? <button className='check-wind-text-btn' onClick={() => setPlacing(false)}>Keep it where it was</button> : null}
          </>
        ) : (
          <>
            <div className='check-wind-pills'>
              <button className='check-wind-pill' onClick={() => setPlacing(true)}><MoveIcon />Move</button>
              {offCentre ? <button className='check-wind-pill' onClick={() => surface.centreOn(origin)}>Re-center</button> : null}
              <button className={`check-wind-pill${thermOn && pro ? ' on' : ''}${pro ? '' : ' locked'}`} onClick={tapThermals}>
                <HeatIcon />Thermals
                {pro ? null : <span className='check-wind-pro'>PRO</span>}
              </button>
            </div>
            <div className='check-wind-risk'>
              <div className='check-wind-risk-labels'><span>Strong</span><span className='strong'>Detection Risk</span><span>Faint</span></div>
              <div className='check-wind-risk-bar' />
              <div className='check-wind-risk-note'>Deer can smell all zones</div>
            </div>
            <div className='check-wind-time'>
              <div className='check-wind-time-head'>
                <span className='check-wind-when'>{whenLabel(mode, k, entry, tz)}</span>
                <div className='check-wind-seg check-wind-days'>
                  {dayList.map((d, i) => (
                    <button key={d.date} className={`${i === day ? 'on' : ''}${i > 0 && !pro ? ' locked' : ''}`} onClick={() => pickDay(i)}>{d.label}</button>
                  ))}
                </div>
              </div>
              <input className='check-wind-range' type='range' min={min} max={max} step='1' value={Math.max(min, Math.min(k, max))}
                style={{ '--cw-now': `${last ? base / last * 100 : 0}%` }}
                disabled={!entries || entries.length === 0}
                onChange={e => setSlider(parseInt(e.target.value, 10))} />
              <div className='check-wind-ticks'>
                {tickList.map((t, i) => (
                  <span key={i} className={`check-wind-tick${t.major ? ' major' : ''}`} style={{ left: `${last ? i / last * 100 : 0}%` }}>
                    <i />{t.label}
                  </span>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {field && kind === '3d'
        ? <ThermalsOverlay3D map={theMap} field={field} every={2} opacity={THERMALS_OPACITY} show={THERMALS_SHOW} />
        : null}
      {thermalsLive && kind !== '3d' && entry ? <WindFlow timeStamp={entry.timeStamp} /> : null}
      {proOpen ? <div className='check-wind-modal'><SubPopup hidePopup={() => setProOpen(false)} /></div> : null}
    </>,
    surface.host
  )
}

const mapStateToProps = state => ({
  mapReference: typeof state.mapReference === 'function' ? state.mapReference() : null,
  is3d: !!state.is3dMapselected,
  token: state.auth.token,
  user: state.user,
  units: state.userPreferences && state.userPreferences.units
})

const mapDispatchToProps = dispatch => ({
  blockFeatures: () => dispatch(blockFeatures()),
  unBlockFeatures: () => dispatch(unBlockFeatures())
})

export default connect(mapStateToProps, mapDispatchToProps)(CheckWind)
